import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  submitAuthenticatedPqr,
  submitGuestPqr,
  toPqrClientError,
} from "@/features/pqr/api/pqrClient";
import { FileUploadChips } from "@/features/pqr/components/FileUploadChips";
import {
  pqrCategories,
  pqrMaxAttachments,
  type PqrSubmissionDraft,
  type PqrSubmissionResult,
} from "@/features/pqr/pqr.types";
import {
  getPqrErrorTranslationKey,
  pqrSubmissionSchema,
  type PqrFormValues,
  type PqrSubmissionValues,
  validatePqrAttachmentFiles,
} from "@/features/pqr/validation/pqrSchemas";

const LINE_INPUT_CLASS_NAME =
  "h-auto rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] text-ink shadow-none placeholder:text-[#b7aa9a] focus:border-clay/45 focus:ring-0";
const LINE_TEXTAREA_CLASS_NAME =
  "min-h-[13rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-8 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45";
const LINE_SELECT_TRIGGER_CLASS_NAME =
  "h-auto rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-left text-[1.05rem] text-ink shadow-none focus:ring-0 data-[placeholder]:text-[#b7aa9a]";

type PQRFormProps = {
  defaultValues?: Partial<PqrSubmissionDraft>;
  isAuthenticated: boolean;
  onSubmitted?: (result: PqrSubmissionResult) => void;
};

export function PQRForm({ defaultValues, isAuthenticated, onSubmitted }: PQRFormProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<PqrSubmissionResult | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<PqrFormValues, undefined, PqrSubmissionValues>({
    defaultValues: {
      category: defaultValues?.category ?? "bug_report",
      email: defaultValues?.email ?? "",
      message: defaultValues?.message ?? "",
      name: defaultValues?.name ?? "",
      subject: defaultValues?.subject ?? "",
    },
    resolver: zodResolver(pqrSubmissionSchema),
  });

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    form.reset({
      category: defaultValues.category ?? "bug_report",
      email: defaultValues.email ?? "",
      message: defaultValues.message ?? "",
      name: defaultValues.name ?? "",
      subject: defaultValues.subject ?? "",
    });
  }, [defaultValues, form]);

  const acceptValue = useMemo(
    () => [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ].join(","),
    [],
  );

  function setAttachmentError(message: string | null) {
    form.clearErrors("root");
    if (message) {
      form.setError("root", {
        message,
      });
    }
  }

  function handleFilesSelected(selected: FileList | null) {
    if (!selected) {
      return;
    }

    const nextFiles = [...files, ...Array.from(selected)].slice(0, pqrMaxAttachments);
    const attachmentError = validatePqrAttachmentFiles(nextFiles);
    if (attachmentError) {
      setAttachmentError(
        attachmentError.fileName
          ? t(getPqrErrorTranslationKey(attachmentError.code), { fileName: attachmentError.fileName })
          : t(getPqrErrorTranslationKey(attachmentError.code)),
      );
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    setAttachmentError(null);
    setFiles(nextFiles);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function handleRemoveFile(index: number) {
    const nextFiles = files.filter((_, fileIndex) => fileIndex !== index);
    setFiles(nextFiles);
    setAttachmentError(null);
  }

  async function onSubmit(values: PqrSubmissionValues) {
    setSubmitError(null);
    setSuccessResult(null);

    const attachmentError = validatePqrAttachmentFiles(files);
    if (attachmentError) {
      setAttachmentError(
        attachmentError.fileName
          ? t(getPqrErrorTranslationKey(attachmentError.code), { fileName: attachmentError.fileName })
          : t(getPqrErrorTranslationKey(attachmentError.code)),
      );
      return;
    }

    try {
      const result = isAuthenticated
        ? await submitAuthenticatedPqr(values, files)
        : await submitGuestPqr(values, files);

      setSuccessResult(result);
      setFiles([]);
      form.reset({
        category: "bug_report",
        email: defaultValues?.email ?? "",
        message: "",
        name: defaultValues?.name ?? "",
        subject: "",
      });
      onSubmitted?.(result);
    } catch (error) {
      const clientError = toPqrClientError(error);
      setSubmitError(
        clientError.fileName
          ? t(getPqrErrorTranslationKey(clientError.code), { fileName: clientError.fileName })
          : t(getPqrErrorTranslationKey(clientError.code)),
      );
    }
  }

  return (
    <div className="space-y-8">
      {successResult ? (
        <div className="rounded-[1.5rem] border border-olive/20 bg-[#edf1e4] px-5 py-4 text-sm text-ink">
          <p className="font-semibold text-olive">{t("pqr.success.title")}</p>
          <p className="mt-1">{t("pqr.success.body", { ticketRef: successResult.ticketRef })}</p>
        </div>
      ) : null}

      <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-x-8 gap-y-6 border-b border-ink/10 pb-8 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-name">
              {t("pqr.fields.name")}
            </Label>
            <Input className={LINE_INPUT_CLASS_NAME} id="pqr-name" {...form.register("name")} />
            {form.formState.errors.name ? (
              <FormMessage>{t(form.formState.errors.name.message ?? "pqr.errors.generic")}</FormMessage>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-email">
              {t("pqr.fields.email")}
            </Label>
            <Input
              className={LINE_INPUT_CLASS_NAME}
              id="pqr-email"
              type="email"
              {...form.register("email")}
            />
            {form.formState.errors.email ? (
              <FormMessage>{t(form.formState.errors.email.message ?? "pqr.errors.generic")}</FormMessage>
            ) : null}
          </div>
        </div>

        <div className="space-y-2 border-b border-ink/10 pb-8">
          <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-category">
            {t("pqr.fields.category")}
          </Label>
          <Controller
            control={form.control}
            name="category"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger aria-label={t("pqr.fields.category")} id="pqr-category" className={LINE_SELECT_TRIGGER_CLASS_NAME}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pqrCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {t(`pqr.categories.${category}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.category ? (
            <FormMessage>{t(form.formState.errors.category.message ?? "pqr.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div className="space-y-2 border-b border-ink/10 pb-8">
          <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-subject">
            {t("pqr.fields.subject")}
          </Label>
          <Input className={LINE_INPUT_CLASS_NAME} id="pqr-subject" {...form.register("subject")} />
          {form.formState.errors.subject ? (
            <FormMessage>{t(form.formState.errors.subject.message ?? "pqr.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div className="space-y-2 border-b border-ink/10 pb-8">
          <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-message">
            {t("pqr.fields.message")}
          </Label>
          <textarea
            id="pqr-message"
            className={LINE_TEXTAREA_CLASS_NAME}
            {...form.register("message")}
          />
          {form.formState.errors.message ? (
            <FormMessage>{t(form.formState.errors.message.message ?? "pqr.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div className="space-y-4 border-b border-ink/10 pb-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <Label className="mb-0 text-base font-semibold text-ink" htmlFor="pqr-files">
                {t("pqr.fields.attachments")}
              </Label>
              <p className="text-sm leading-6 text-ink/55">{t("pqr.fields.attachmentsHelp")}</p>
            </div>
            <Button
              className="shrink-0"
              onClick={() => inputRef.current?.click()}
              type="button"
              variant="outline"
            >
              {t("pqr.actions.addAttachment")}
            </Button>
          </div>

          <input
            ref={inputRef}
            accept={acceptValue}
            className="hidden"
            data-testid="pqr-file-input"
            id="pqr-files"
            multiple
            type="file"
            onChange={(event) => handleFilesSelected(event.target.files)}
          />

          <FileUploadChips files={files} onRemove={handleRemoveFile} />
        </div>

        {form.formState.errors.root?.message ? (
          <FormMessage>{form.formState.errors.root.message}</FormMessage>
        ) : null}
        {submitError ? <FormMessage>{submitError}</FormMessage> : null}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="max-w-2xl text-sm leading-6 text-ink/55">{t("pqr.form.note")}</p>
          <Button disabled={form.formState.isSubmitting} size="lg" type="submit">
            {form.formState.isSubmitting ? t("common.saving") : t("pqr.actions.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
